type PasswordStrengthLevel = 'empty' | 'weak' | 'fair' | 'good' | 'strong';

type PasswordStrengthResult = {
  level: PasswordStrengthLevel;
  /** 0 when empty, otherwise 1–4 (weak → strong). */
  score: number;
  /** Number of filled bar segments (0–4). */
  filledSegments: number;
};

export const PASSWORD_STRENGTH_SEGMENT_COUNT = 4;

/**
 * Heuristic password strength for UI nudging only — not a substitute for server-side policy.
 * Rewards length (8 / 12 / 16) and character variety (lower, upper, digit, special).
 */
export const getPasswordStrength = (password: string): PasswordStrengthResult => {
  if (!password) {
    return { level: 'empty', score: 0, filledSegments: 0 };
  }

  const points = [
    password.length >= 8,
    password.length >= 12,
    password.length >= 16,
    /[a-z]/.test(password),
    /[A-Z]/.test(password),
    /\d/.test(password),
    /[^A-Za-z0-9]/.test(password)
  ].filter(Boolean).length;

  // points 0–7 → score 1–4
  const score = points <= 2 ? 1 : points <= 3 ? 2 : points <= 5 ? 3 : 4;

  const levelByScore: Record<1 | 2 | 3 | 4, Exclude<PasswordStrengthLevel, 'empty'>> = {
    1: 'weak',
    2: 'fair',
    3: 'good',
    4: 'strong'
  };

  return {
    level: levelByScore[score],
    score,
    filledSegments: Math.min(score, PASSWORD_STRENGTH_SEGMENT_COUNT)
  };
};
